/**
 * Shared Formatting Utilities for Smart-Billing
 * Centralizes currency, numbers, durations, and timezone-aware dates.
 */
import moment from 'moment';

/**
 * Format number to Indonesian Rupiah standard (e.g., "Rp 1.250.000")
 */
export const formatCurrency = (val?: number | null, fallback = 'Rp 0'): string => {
  if (val === undefined || val === null || isNaN(val)) return fallback;
  return `Rp ${Math.round(val).toLocaleString('id-ID')}`;
};

/**
 * Format currency with decimal precision for small unit rates (e.g., "Rp 125,50")
 */
export const formatCurrencyDecimal = (val?: number | null, decimals = 2, fallback = 'Rp 0'): string => {
  if (val === undefined || val === null || isNaN(val)) return fallback;
  return `Rp ${Number(val).toLocaleString('id-ID', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

/**
 * Format large numbers compactly in IDR (e.g., "Rp 1.5M", "Rp 250rb")
 */
export const formatShortIdr = (val?: number | null, fallback = 'Rp 0'): string => {
  if (!val || isNaN(val)) return fallback;
  if (Math.abs(val) >= 1_000_000_000) {
    return `Rp ${(val / 1_000_000_000).toFixed(2)}Miliar`;
  }
  if (Math.abs(val) >= 1_000_000) {
    return `Rp ${(val / 1_000_000).toFixed(2)}Jt`;
  }
  if (Math.abs(val) >= 1_000) {
    return `Rp ${Math.round(val / 1_000).toLocaleString('id-ID')}rb`;
  }
  return `Rp ${Math.round(val).toLocaleString('id-ID')}`;
};

/**
 * Format number in millions with 1 decimal (e.g. 1.5)
 */
export const formatMillions = (val?: number | null): string => {
  if (val === undefined || val === null || isNaN(val)) return '0.0';
  return (val / 1_000_000).toFixed(1);
};

/**
 * Format duration in seconds to standard human-readable format (e.g. "78s", "2m 15s", "1h 05m 20s")
 */
export const formatDurationSeconds = (seconds?: number | null): string => {
  if (seconds === undefined || seconds === null || isNaN(seconds) || seconds <= 0) {
    return '0s';
  }
  const s = Math.round(seconds);
  if (s < 60) {
    return `${s}s`;
  }
  const minutes = Math.floor(s / 60);
  const remSec = s % 60;
  if (minutes < 60) {
    return `${minutes}m ${remSec > 0 ? `${remSec}s` : ''}`.trim();
  }
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return `${hours}j ${remMin}m ${remSec > 0 ? `${remSec}s` : ''}`.trim();
};

/**
 * Format date display consistently with explicit WIB timezone awareness
 */
export const formatDateTimeWib = (dateStr?: string | Date | null, format = 'DD MMM YYYY, HH:mm'): string => {
  if (!dateStr) return '-';
  const m = moment(dateStr);
  if (!m.isValid()) return '-';
  return m.format(format);
};

/**
 * Format date only (e.g., "26 Okt 2026")
 */
export const formatDateWib = (dateStr?: string | Date | null, format = 'DD MMM YYYY'): string => {
  if (!dateStr) return '-';
  const m = moment(dateStr);
  if (!m.isValid()) return '-';
  return m.format(format);
};
